import { dateFormatter } from "@/components/DateFormat/DateFormat";
import { MODAL_ID } from "@/lib/constants";
import NewCustomerModal from "@/pages/MyCustomer/MyCustomer/NewCustomerModal/NewCustomerModal";
import { useAppDispatch } from "@/store/hooks";
import type { RootState } from "@/store/rootReducer";
import {
  getCountryList,
  getCustomerData,
  getLeadDesignations,
  getLeadSource,
  getLeadStage,
  getLeadType,
  getReferalUser,
  getTerritories,
  updateCustomerDetails,
  setRefreshCustomerList,
  getCustomFields,
  getCustomerConfigurationCustomFields,
  getCRMSettings,
} from "@/store/slices/customerSlice";
import { getTagList } from "@/store/slices/dealAndPipelineSlice";
import { setModalOpen } from "@/store/slices/globalSlice";
import { Box, Button, Checkbox, Chip, Divider, TextField, Typography, CircularProgress, InputAdornment, IconButton } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import toast from "@/lib/toast";
import { extractFileInfo, formatUrl, type AttachmentItem } from "@/lib/methodUtils";
import AttachmentPreview from "@/components/AttachmentPreview/AttachmentPreview";
import ViewLocation from "@/components/ViewLocation/ViewLocation";

const Details = ({ LeadId }: { LeadId: number }) => {
  const dispatch = useAppDispatch();
  const detailsScrollRef = useRef<HTMLDivElement | null>(null);
  const { customerData, customFields, leadSource, statusList, leadStage, leadType, Territory, ReferralUser, countryList, leadDesignation, crmSettings, loading } = useSelector((state: RootState) => state.customer);
  const { modalOpen } = useSelector((state: RootState) => state.global);

  const fetchCustomerData = useCallback(() => {
    if (LeadId) {
      dispatch(getCustomerData(LeadId));
    }
  }, [LeadId, dispatch]);

  useEffect(() => {
    fetchCustomerData();
  }, [fetchCustomerData]);

  useEffect(() => {
    detailsScrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [LeadId]);

  useEffect(() => {
    dispatch(getTagList(2));
    dispatch(getLeadSource());
    dispatch(getLeadStage());
    dispatch(getLeadType());
    dispatch(getTerritories());
    dispatch(getReferalUser());
    dispatch(getCountryList());
    dispatch(getLeadDesignations());
    dispatch(getCustomFields());
    dispatch(getCustomerConfigurationCustomFields());
    dispatch(getCRMSettings());
  }, [dispatch]);

  const viewLocation = useCallback((lat: string, lng: string) => {
    dispatch(setModalOpen({ id: "ViewLocation", data: { lat, lng } }));
  }, [dispatch]);

  const renderField = (
    label: string,
    value: any,
    isAttachment = false,
    isLocation = false,
    lat = '',
    lng = '',
  ) => (
    <div key={label}>
      {isAttachment ? (
        <>
          <Typography variant="body2" color="text.secondary" mb={1}>
            {label}
          </Typography>

          {value ? (
            <AttachmentPreview
              attachments={getAttachment(value, label)}
              showAttachmentLabel={false}
            />
          ) : (
            <Typography>-</Typography>
          )}
        </>
      ) : (
        <Box sx={{ position: "relative" }}>
          <TextField
            label={label}
            value={value || "-"}
            variant="standard"
            size="small"
            fullWidth
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: isLocation && lat && lng ? (
                  <InputAdornment
                    position="end"
                    sx={{
                      alignSelf: "center",
                      mt: "-12px",
                      mr: "10px",
                    }}
                  >
                    <IconButton
                      edge="end"
                      tabIndex={-1}
                      onClick={(e) => {
                        e.preventDefault();
                        viewLocation(lat, lng)
                      }}
                    >
                      <img src="./assets/media/locate.svg" width={16} height={16} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
        </Box>
      )}
    </div>
  );

  const mergedCustomFields = useMemo(() => {
    if (!customFields?.length || !customerData?.CustomFields?.length) return [];

    return customFields
      .map((config: any) => {
        const selected = customerData.CustomFields.find(
          (cf: any) => cf.CustomFieldId === config.Id
        );
        if (!selected) return null;

        let displayValue = "-";

        switch (config.FieldType) {
          case "Text":
          case "Quantitative":
          case "Date":
            displayValue = selected.CustomFieldValue || "-";
            break;

          case "Single":
          case "YesNo":
            displayValue =
              config.Options?.find(
                (o: any) => o.Id === selected.OptionId?.[0]
              )?.Value || "-";
            break;

          case "Multiple":
            displayValue =
              config.Options
                ?.filter((o: any) =>
                  selected.OptionId?.includes(o.Id)
                )
                .map((o: any) => o.Value)
                .join(", ") || "-";
            break;

          case "Image":
          case "CameraImage":
          case "Signature":
            displayValue = selected.CustomFieldValue || "-";
            break;

          default:
            displayValue = "-";
        }

        return {
          Id: config.Id,
          Name: config.Name,
          FieldType: config.FieldType,
          displayValue,
        };
      })
      .filter(Boolean);
  }, [customFields, customerData?.CustomFields]);

  const handleUpdateCustomer = async (payload: any) => {
    await dispatch(updateCustomerDetails(payload)).unwrap();
    toast.success("Customer Updated Successfully");
    fetchCustomerData();
    dispatch(setRefreshCustomerList(true));
  };

  const getAttachment = (path?: string, label?: string): AttachmentItem[] => {

    if (!path) return [];

    const { name, extension } = extractFileInfo(path);

    return [
      {
        label: label || "",
        name,
        extension,
        url: formatUrl(path),
      },
    ];
  };

  if (loading?.getCustomerData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      ref={detailsScrollRef}
      sx={{ px: 1.75, py: 2.5, maxHeight: "calc(100vh - 122px)", overflowY: "auto" }}
    >
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          onClick={() => dispatch(setModalOpen({ id: MODAL_ID.EDITCUSTOMER, data: null }))}
        >
          Edit
        </Button>
      </Box>

      {/* Basic Info */}
      <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
        {renderField("Lead/Customer Name", customerData?.LeadName)}
        {renderField("Ref No.", customerData?.RefNo)}
        {renderField("Created By", customerData?.CreatedBy)}
        {renderField("Created Date & Time", dateFormatter(customerData?.CreatedDate, "dd MMM yyyy hh:mm a"))}
        {renderField("Modified By", customerData?.ModifiedBy)}
        {renderField("Modified Date & Time", dateFormatter(customerData?.ModifiedDate, "dd MMM yyyy hh:mm a"))}
      </Box>

      {/* Primary Contact */}
      <Typography sx={{ mt: 3, fontWeight: 600, fontSize: "15px", mb: 1 }}>Primary Contact</Typography>
      <Divider />
      <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mt={2}>
        {renderField("Contact Person Name", customerData?.PersonName)}
        {renderField("Referred By", customerData?.ReferredByName)}
        {renderField("Mobile Number", customerData?.MobileNo)}
        {renderField("Telephone Number", customerData?.ContactNo)}
        {renderField("Email", customerData?.Email)}
        {renderField("Contact Person Designation", customerData?.PersonDesignation)}
        {renderField("Contact Person Location", customerData?.PersonLocation)}
      </Box>

      {customerData?.LeadContacts?.length > 0 && (
        <>
          {customerData.LeadContacts?.filter((contact: any) => contact?.IsActive).map((contact: any, index: number) => (
            <Box key={contact?.Id ?? `${contact.Name}-${index}`}>
              <Typography sx={{ mt: 3, fontWeight: 600, fontSize: "15px", mb: 1 }}>
                Alternate Contact {index + 1}
              </Typography>
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} sx={{ mt: 2 }}>
                {renderField("Contact Person Name", contact.Name)}
                {renderField("Contact Number", contact.ContactNo)}
                {renderField("Email", contact.Email)}
                {renderField("Contact Person Designation", contact.Designation)}
                {renderField("Contact Person Location", contact.Location)}
              </Box>
            </Box>
          ))}
        </>
      )}

      {/* Address */}
      <Typography sx={{ mt: 3, fontWeight: 600, fontSize: "15px", mb: 1 }}>Customer Address</Typography>
      <Divider />
      <Box display="grid" gridTemplateColumns="1fr" gap={2} mt={2}>
        {renderField("Address", customerData?.Address, false, true, customerData?.Latitude, customerData?.Longitude)}
      </Box>

      <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mt={2}>
        {renderField("Country", customerData?.CountryName)}
        {renderField("State", customerData?.StateName)}
        {renderField("City", customerData?.CityName)}
        {renderField("Locality", customerData?.LocalityName)}
        {renderField("Pin Code", customerData?.PinCode)}
      </Box>

      {/* More Info */}
      <Typography sx={{ mt: 3, fontWeight: 600, fontSize: "15px", mb: 1 }}>More Info</Typography>
      <Divider />
      <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mt={2}>
        {renderField("Customer Source", customerData?.LeadSourceName)}
        {renderField("Customer Stage", customerData?.LeadStageName)}
        <Box>
          <Typography color="text.secondary">Tags</Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
            {customerData?.LeadTags?.map((tag: any) => (
              <Chip key={tag.Id} label={tag.TagName} size="small" />
            ))}
          </Box>
        </Box>
        {renderField("Territory", customerData?.TerritoryName)}
        {renderField("Website (Optional)", customerData?.Website)}
        {renderField("Other Information (Optional)", customerData?.OtherInfo)}
        {renderField("Front Visiting Card", customerData?.FrontView, true)}
        {renderField("Back Visiting Card", customerData?.BackView, true)}
        <Box display="flex" alignItems="center" gap={1}>
          <Checkbox checked={customerData?.IsVisibleToAll} disabled />
          <Typography>Make Visible to all team members</Typography>
        </Box>
      </Box>

      {/* Primary-Secondary Sales Info */}
      <Typography sx={{ mt: 3, fontWeight: 600, fontSize: "15px", mb: 1 }}>Primary-Secondary Sales Info</Typography>
      <Divider />
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} sx={{ mt: 2 }}>
        {renderField("Custom Type", customerData?.LeadTypeName)}
        {renderField("Distributor/Customer Parent Name", customerData?.ParentLeadName)}
      </Box>

      {mergedCustomFields.length > 0 && (
        <>
          <Typography sx={{ mt: 3, fontWeight: 600, fontSize: "15px", mb: 1 }}>
            Custom Fields
          </Typography>
          <Divider />
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={2}>
            {mergedCustomFields.map((field: any) => {
              if (
                ["Image", "CameraImage", "Signature"].includes(field.FieldType) &&
                field.displayValue !== "-"
              ) {
                return (
                  <Box key={field.Id}>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      {field.Name}
                    </Typography>
                    <AttachmentPreview
                      key={field.Id}
                      attachments={getAttachment(field.displayValue, field.Name)}
                      showAttachmentLabel={false}
                    />
                  </Box>
                );
              }

              return renderField(field.Name, field.displayValue);
            })}
          </Box>
        </>
      )}

      <NewCustomerModal
        open={modalOpen.id === MODAL_ID.EDITCUSTOMER}
        action="edit"
        data={customerData}
        statusList={statusList}
        sourceList={leadSource}
        stageList={leadStage}
        typeList={leadType}
        territoryList={Territory?.Territory || []}
        referralList={ReferralUser?.results || []}
        countryList={countryList}
        leadDesignation={leadDesignation}
        crmSettings={crmSettings}
        closeModal={() => dispatch(setModalOpen({ id: null, data: null }))}
        onSubmit={handleUpdateCustomer}
      />

      <ViewLocation
        title="Location"
        width="800px"
        modalOpen={modalOpen.id === "ViewLocation"}
        closeModal={() => dispatch(setModalOpen({ id: null, data: null }))}
        lat={modalOpen?.data?.lat}
        lng={modalOpen?.data?.lng}
      />
    </Box>
  );
}

export default memo(Details);